import { CalendarTodayOutlined } from '@mui/icons-material';
import Close from '@mui/icons-material/Close';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  styled,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box } from '@mui/system';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import Localization from 'l18n/l18n';
import Appointments from 'models/appointments';
import * as React from 'react';
import { RRule } from 'rrule';

const StyledTextField = styled(TextField)({
  border: '0 !important',
  borderRadius: 0,
  background: '#eeeeee',
  width: '100%',

  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      border: 0, // - Set the Input border
    },
  },
});
const weekDays = [
  { id: 'SU', label: Localization.translate['days_short']['sun'] },
  { id: 'MO', label: Localization.translate['days_short']['mon'] },
  { id: 'TU', label: Localization.translate['days_short']['tue'] },
  { id: 'WE', label: Localization.translate['days_short']['wed'] },
  { id: 'TH', label: Localization.translate['days_short']['thu'] },
  { id: 'FR', label: Localization.translate['days_short']['fri'] },
  { id: 'SA', label: Localization.translate['days_short']['sat'] },
];
 
const RRule_i18n_options = {
  getText: (id) => Localization.translate['recurrence_strings'][id] || id,
  language: {
    dayNames: Object.entries(Localization.translate['days_short']).map(day=>day[1]),
    monthNames: Object.entries(Localization.translate['months']).map(month=>month[1])
  },
  dateFormat: (year, month, day) => `(${day} ${month} ${year})`
};
const posDays = Localization.translate['repeat_positions'];
const getRRuleDay = (day) => {
  switch (day) {
    case 'SU':
    case 1:
      return RRule.SU;
      break;
    case 'MO':
    case 2:
      return RRule.MO;
      break;
    case 'TU':
    case 3:
      return RRule.TU;
      break;
    case 'WE':
    case 4:
      return RRule.WE;
      break;
    case 'TH':
    case 5:
      return RRule.TH;
      break;
    case 'FR':
    case 6:
      return RRule.FR;
      break;
    default:
      return RRule.SA;
  }
};
const ExceptionsDialog = React.forwardRef((props, ref) => {
  const { handleCancel } = props;
  const [dates, setDates] = React.useState({
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD'),
  });
  const [time, setTime] = React.useState({
    start_time: dayjs().format('HH:mm:ss'),
    end_time: dayjs().format('HH:mm:ss'),
  });
  const [currentView, setCurrentView] = React.useState('daily');
  const [exceptionName, setExceptionName] = React.useState('');
  const [repeatCheckbox, setRepeatCheckbox] = React.useState(false);
  const [allDayCheckbox, setAllDayCheckbox] = React.useState(false);
  const [recurrenceInterval, setRecurrenceInterval] = React.useState(1);
  const [repeatOnRadio, setRepeatOnRadio] = React.useState(1);
  const [repeatOnDayValue, setRepeatOnDayValue] = React.useState(1);
  const [repeatOnMonthValue, setRepeatOnMonthValue] = React.useState(1);
  const [selectedWeekDays, setSelectedWeekDays] = React.useState(() => ['SU']);
  const [onPosDay, setOnPosDay] = React.useState(1);
  const [onPosDayName, setOnPosDayName] = React.useState(1);
  const [onPosMonth, setOnPosMonth] = React.useState(1);
  const [rruleString, setRRuleString] = React.useState('');
  const [rruleText,setRRuleText] = React.useState('');

  React.useEffect(() => {
    if (allDayCheckbox == true) {
      setTime({
        start_time: dayjs().startOf('d').format('HH:mm:ss'),
        end_time: dayjs().endOf('d').format('HH:mm:ss'),
      });
    }
  }, [allDayCheckbox]);

  React.useEffect(() => {
    const byyearday = repeatOnRadio == '2' ? [repeatOnDayValue] : [];
    const [startYear,startMonth,startDay] = dates.start_date.split('-') ;
    const [endYear,endMonth,endDay] = dates.end_date.split('-') ;
    const [startHour,startMin] = dayjs(time.start_time,'HH:mm:ss').format('HH-mm').split('-');
    const [endHour,endMin] = dayjs(time.end_time,'HH:mm:ss').format('HH-mm').split('-');

     let rruleConfig = {
      freq:
        currentView == 'yearly'
          ? RRule.YEARLY
          : currentView == 'monthly'
          ? RRule.MONTHLY
          : currentView == 'weekly'
          ? RRule.WEEKLY
          : RRule.DAILY,
      interval: Number(recurrenceInterval),
      dtstart:new Date(Date.UTC(startYear, startMonth -1 , startDay, startHour, startMin, 0)),
      until: new Date(Date.UTC(endYear, endMonth -1, endDay, endHour, endMin, 0)),
      wkst: RRule.SU,
      byyearday,
    };
    if (currentView == 'weekly') {
      rruleConfig = Object.assign({
        ...rruleConfig,
        byweekday: selectedWeekDays.map((day, i) => {
          return getRRuleDay(day);
        }),
      });
    } else {
      delete rruleConfig['byweekday'];
    }

    if (currentView == 'monthly') {
      if (repeatOnRadio == '1') {
        rruleConfig = Object.assign({
          ...rruleConfig,
          bymonthday: Number(repeatOnDayValue),
        });
      } else {
        delete rruleConfig['bymonthday'];
        rruleConfig = Object.assign({
          ...rruleConfig,
          bysetpos: [onPosDay],
          byweekday: getRRuleDay(onPosDayName),
        });
      }
    } else {
      delete rruleConfig['bymonthday'];
      delete rruleConfig['onPosDayName'];
      delete rruleConfig['bysetpos'];
    }
    if (currentView == 'yearly') {
      if (repeatOnMonthValue > 0) {
        rruleConfig = Object.assign({
          ...rruleConfig,
          bymonth: Number(repeatOnMonthValue),
        });
      } else {
        delete rruleConfig['bymonth'];
      }
      if (repeatOnRadio == '1') {
        rruleConfig = Object.assign({
          ...rruleConfig,
          bymonthday: Number(repeatOnDayValue),
          bymonth: Number(repeatOnMonthValue),
        });
      } else {
        delete rruleConfig['byyearday'];
        delete rruleConfig['bymonth'];
        delete rruleConfig['bymonthday'];
        rruleConfig = Object.assign({
          ...rruleConfig,
          byweekday: getRRuleDay(onPosDayName),
          bymonth: Number(onPosMonth),
          bysetpos: Number(onPosDay)
        });
      }
    } else {
      delete rruleConfig['byyearday'];
      delete rruleConfig['bymonth'];
      if (currentView !== 'monthly') {
        delete rruleConfig['bysetpos'];
      }
    }

    const rrule = new RRule(rruleConfig);
    setRRuleString(rrule.toString());
    setRRuleText(rrule.toText(RRule_i18n_options.getText,RRule_i18n_options.language,RRule_i18n_options.dateFormat));
  }, [
    dates,
    currentView,
    time,
    repeatCheckbox,
    allDayCheckbox,
    recurrenceInterval,
    repeatOnRadio,
    repeatOnDayValue,
    repeatOnMonthValue,
    selectedWeekDays,
    onPosDay,
    onPosDayName,
    onPosMonth,
  ]);
  const changeWeekDay = (event, daysSelected) => {
    if (daysSelected.length >= 1) {
      setSelectedWeekDays(daysSelected);
    }
  };

  const intervalSuffix = () => {
    let intervalText = Localization.translate['repeat_day'];
    if (currentView == 'daily') {
      if (recurrenceInterval < 3) {
        intervalText = Localization.translate['repeat_day'];
      } else {
        intervalText =  Localization.translate['repeat_days'];
      }
    } else {
      if (currentView == 'weekly') {
        if (recurrenceInterval < 3) {
          intervalText =  Localization.translate['repeat_week'];
        } else {
          intervalText = Localization.translate['repeat_weeks'];
        }
      } else {
        if (currentView == 'monthly') {
          if (recurrenceInterval < 3) {
            intervalText = Localization.translate['repeat_month'];
          } else {
            intervalText = Localization.translate['repeat_months'];;
          }
        } else {
          intervalText = Localization.translate['repeat_year'];;
        }
      }
    }

    return intervalText;
  };
  React.useImperativeHandle(ref, () => ({
    save() {
      
      Appointments.changeExceptionsDays({
        ...dates,
        exception_note: exceptionName,
        start_time: dayjs(time.start_time).isValid() ? dayjs(time.start_time).format('HH:mm:ss'): time.start_time,
        end_time:  dayjs(time.end_time).isValid() ? dayjs(time.end_time).format('HH:mm:ss'): time.end_time,
        is_all_day: allDayCheckbox,
        repeat:repeatCheckbox,
        rrule: rruleString,
        repeat_type:currentView,
        repeat_every: recurrenceInterval
      });

 
    },
  }));

  const onDateChange = (newDate, type) => {
    newDate = newDate.format('YYYY-MM-DD');
    switch (type) {
      case 2:
        setDates((previous) => {
          return { ...previous, end_date: newDate };
        });
        break;
      default:
        setDates((previous) => {
          return { ...previous, start_date: newDate };
        });
    }
  };

  const onTimeChange = (newTime, type) => {
    switch (type) {
      case 2:
        setTime((previous) => {
          return { ...previous, end_time: dayjs(newTime)  };
        });
        break;
      default:
        setTime((previous) => {
          return { ...previous, start_time: dayjs(newTime)  };
        });
    }
  };

  const handleRepeatCheckboxChange = React.useCallback(
    (e) => {
      setRepeatCheckbox(e.target.checked);
    },
    [repeatCheckbox]
  );

  const handleRepeatViewChange = React.useCallback(
    (e) => {
      if (e.target.value == 'yearly') {
        setRecurrenceInterval(1);
      }
      setCurrentView(e.target.value);
    },
    [currentView, recurrenceInterval]
  );
  const handleAllDayChange = React.useCallback(
    (e) => {
      setAllDayCheckbox(e.target.checked);
    },
    [allDayCheckbox]
  );
  const handleRecurrenceIntervalChange = React.useCallback(
    (e) => {
      const intervalValue = currentView == 'yearly' ? 1 : e.target.value;
      if (intervalValue <= 99 && intervalValue > 0) {
        setRecurrenceInterval(intervalValue);
      }
    },
    [currentView, recurrenceInterval]
  );

  const handleRepeatOnRadioChange = React.useCallback(
    (e) => {
      setRepeatOnRadio(e.target.value);
    },
    [repeatOnRadio]
  );

  const handleRepeatOnDayValueChange = React.useCallback(
    (e) => {
      if (e.target.value <= 31 && e.target.value > 0) {
        setRepeatOnDayValue(e.target.value);
      }
    },
    [repeatOnDayValue]
  );
  const handleRepeatMonthValueChange = React.useCallback(
    (e) => {
      const value = e.target.value;
      if (value <= 12 && value > 0) {
        setRepeatOnMonthValue(value);
      }
    },
    [repeatOnMonthValue]
  );

  const handlePosDayChange = React.useCallback(
    (e) => {
      setOnPosDay(e.target.value);
    },
    [onPosDay]
  );
  const handleOnPosDayNameChange = React.useCallback(
    (e) => {
      setOnPosDayName(e.target.value);
    },
    [onPosDayName]
  );
  const handleOnPosMonthChange = React.useCallback(
    (e) => {
      setOnPosMonth(e.target.value);
    },
    [onPosMonth]
  );
  let RepeatOnComponent = () => {
    if (currentView == 'daily') {
      return <></>;
    }

    if (currentView == 'weekly') {
      return (
        <Grid item xs={12} md={12} lg={12}>
          <Stack spacing={1}>
            <Typography variant="p"> {Localization.translate['recurrence_in_day']}</Typography>
            <ToggleButtonGroup
              color="primary"
              value={selectedWeekDays}
              onChange={changeWeekDay}
              aria-label="text formatting"
            >
              {weekDays.map((day) => (
                <ToggleButton key={day.id} value={day.id} aria-label="bold">
                  {day.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Stack>
        </Grid>
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DialogTitle>
        <Grid container alignItems="center">
          <Grid item xs={11} className="draggable-dialog-title">
            {Localization.translate['manage_exceptions']}
          </Grid>
          <Grid item xs={1}>
            <IconButton onClick={() => handleCancel()}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers dir="rtl">
        <Box sx={{ p: 6 }}>
          {/* 
            //////////////////////
          
               Exception date 

          /////////////////////
          */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Stack spacing={1}>
                <Typography variant="p">
                  {Localization.translate['first_exception_day']}
                </Typography>

                <DatePicker
                  name="appointment_calendar_date"
                  onChange={(event) => onDateChange(event, 1)}
                  value={dates.start_date}
                  components={{
                    OpenPickerIcon: CalendarTodayOutlined,
                  }}
                  OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                  renderInput={(params) => <StyledTextField {...params} />}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Stack spacing={1}>
                <Typography variant="p">
                  {Localization.translate['last_exception_day']}
                </Typography>

                <DatePicker
                  name="appointment_calendar_date"
                  onChange={(event) => onDateChange(event, 2)}
                  value={dates.end_date}
                  OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                  components={{
                    OpenPickerIcon: CalendarTodayOutlined,
                  }}
                  renderInput={(params) => <StyledTextField {...params} />}
                />
              </Stack>
            </Grid>
            {/* 
            ///////////////////////
          
              All day checkbox 

          ///////////////////////
          */}
            <Grid item xs={12} md={12} lg={12}>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={allDayCheckbox}
                      onChange={handleAllDayChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  }
                  label={Localization.translate['all_day_all_time']}
                />
              </Stack>
            </Grid>

            {/* 
            ///////////////////////
          
               Exception time 

          ///////////////////////
          */}
            {!allDayCheckbox && (
              <>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="p">{Localization.translate['exception_start_time']}</Typography>

                    <TimePicker
                      name="appointment_calendar_time"
                      onChange={(event) => onTimeChange(event, 1)}
                      value={time.start_time}
                      components={{
                        OpenPickerIcon: CalendarTodayOutlined,
                      }}
                      OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                      renderInput={(params) => <StyledTextField {...params} />}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack spacing={1}>
                    <Typography variant="p">{Localization.translate['exception_end_time']}</Typography>

                    <TimePicker
                      name="appointment_calendar_time"
                      onChange={(event) => onTimeChange(event, 2)}
                      value={time.end_time}
                      OpenPickerButtonProps={{ style: { color: '#279BFF' } }}
                      components={{
                        OpenPickerIcon: CalendarTodayOutlined,
                      }}
                      renderInput={(params) => <StyledTextField {...params} />}
                    />
                  </Stack>
                </Grid>
              </>
            )}

            {/* 
            ///////////////////////
          
               Exception note 

          ///////////////////////
          */}
            <Grid item xs={12} md={12} lg={12}>
              <Stack spacing={1}>
                <Typography variant="p">
                  {Localization.translate['exception_notes']}
                </Typography>
                <StyledTextField
                  value={exceptionName}
                  onChange={(value) => setExceptionName(value.target.value)}
                  sx={{ width: '100%' }}
                />
              </Stack>
            </Grid>
            {/* 
            ///////////////////////
          
               repeat checkbox 

          ///////////////////////
          */}
            <Grid item xs={12} md={12} lg={12}>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={repeatCheckbox}
                      onChange={handleRepeatCheckboxChange}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  }
                  label={Localization.translate['repeat_exception']}
                />
              </Stack>
            </Grid>

            {/* 
            ///////////////////////
          
               repeat  

          ///////////////////////
          */}
            {repeatCheckbox && (
              <>
                {/* 
           ///////////////////////
          
               repeat  type

          ///////////////////////
               */}
                <Grid item xs={12} md={6} lg={6}>
                  <Stack spacing={1}>
                    <Typography variant="p">{Localization.translate['repeat_type']}</Typography>

                    <StyledTextField
                      select
                      onChange={handleRepeatViewChange}
                      value={currentView}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem key={'daily'} value={'daily'}>
                        {Localization.translate['repeat_types']['daily']}
                      </MenuItem>
                      <MenuItem key={'weekly'} value={'weekly'}>
                      {Localization.translate['repeat_types']['weekly']}
                      </MenuItem>
                      <MenuItem key={'monthly'} value={'monthly'}>
                      {Localization.translate['repeat_types']['monthly']}
                      </MenuItem>
                      <MenuItem key={'yearly'} value={'yearly'}>
                      {Localization.translate['repeat_types']['yearly']}
                      </MenuItem>
                    </StyledTextField>
                  </Stack>
                </Grid>
                {/* 
           ///////////////////////
          
               repeat  every

          ///////////////////////
               */}
                <Grid d item xs={12} md={6} lg={6}>
                  <Stack spacing={1}>
                    <Typography variant="p"> {Localization.translate['repeat_for']} </Typography>
                    <Grid container alignItems="center" gap={1}>
                      <Grid item xs={6}>
                        <StyledTextField
                          type="number"
                          disabled={currentView == 'yearly'}
                          onChange={handleRecurrenceIntervalChange}
                          value={recurrenceInterval}
                          sx={{ width: '100%' }}
                          InputProps={{
                            inputProps: {
                              min: 1,
                              max: currentView === 'yearly' ? 1 : 99,
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        {intervalSuffix()}
                      </Grid>
                    </Grid>
                  </Stack>
                </Grid>

                <RepeatOnComponent />

                {(currentView === 'monthly' || currentView === 'yearly') && (
                  <Grid item xs={12} md={12} lg={12}>
                    <RadioGroup
                      aria-labelledby="demo-controlled-radio-buttons-group"
                      name="controlled-radio-buttons-group"
                      value={repeatOnRadio}
                      onChange={handleRepeatOnRadioChange}
                    >
                      <Stack spacing={1}>
                        <div>
                          <FormControlLabel
                            value={1}
                            sx={{ width: '100%' }}
                            componentsProps={{
                              typography: { style: { width: '100%' } },
                            }}
                            control={<Radio />}
                            label={
                              <Grid
                                container
                                alignItems="center"
                                style={{ width: '100%' }}
                              >
                                <Grid item xs={6} alignItems="center">
                                  <Grid item xs={12} md={6}>
                                    <p> {Localization.translate['recurrence_in_day']}</p>
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <StyledTextField
                                      type="number"
                                      disabled={repeatOnRadio == 2}
                                      onChange={handleRepeatOnDayValueChange}
                                      value={repeatOnDayValue}
                                      sx={{ width: '100%' }}
                                      InputProps={{
                                        inputProps: {
                                          min: 1,
                                          max: 31,
                                        },
                                      }}
                                    />
                                  </Grid>
                                </Grid>
                                {currentView == 'yearly' && (
                                  <Grid item xs={6} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                      <p> {Localization.translate['repeat_in_month']}</p>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                      <StyledTextField
                                        select
                                        disabled={repeatOnRadio == 2}
                                        onChange={handleRepeatMonthValueChange}
                                        value={repeatOnMonthValue}
                                        sx={{ width: '100%' }}
                                      >
                                        {Object.entries(Localization.translate['months']).map(
                                          (month, key) => {
                                            return (
                                              <MenuItem
                                                key={key}
                                                value={key + 1}
                                              >
                                                {month[1]}
                                              </MenuItem>
                                            );
                                          }
                                        )}
                                      </StyledTextField>
                                    </Grid>
                                  </Grid>
                                )}
                              </Grid>
                            }
                          />
                        </div>
                        <div>
                          <FormControlLabel
                            value={2}
                            control={<Radio />}
                            sx={{ width: '100%' }}
                            componentsProps={{
                              typography: { style: { width: '100%' } },
                            }}
                            label={
                              <Grid container alignItems="center">
                                <Grid
                                  item
                                  xs={12}
                                  md={currentView == 'monthly' ? 6 : 4}
                                  alignItems="center"
                                >
                                  <Grid item xs={12} md={6}>
                                    <p>{Localization.translate['repeat_in']}</p>
                                  </Grid>
                                  <Grid item xs={12} md={11}>
                                    <StyledTextField
                                      select
                                      disabled={repeatOnRadio == 1}
                                      onChange={handlePosDayChange}
                                      value={onPosDay}
                                      sx={{ width: '100%' }}
                                    >
                                      {posDays.map((posDay, key) => {
                                        return (
                                          <MenuItem key={key !== 4 ? key : -1} value={key == 4 ?-1 : key + 1}>
                                            {posDay}
                                          </MenuItem>
                                        );
                                      })}
                                    </StyledTextField>
                                  </Grid>
                                </Grid>

                                <Grid
                                  item
                                  xs={12}
                                  md={currentView == 'monthly' ? 6 : 4}
                                  alignItems="center"
                                >
                                  <Grid item xs={12} md={12}>
                                    <p> {Localization.translate['repeat_in_day']}</p>
                                  </Grid>
                                  <Grid item xs={12} md={11}>
                                    <StyledTextField
                                      select
                                      disabled={repeatOnRadio == 1}
                                      onChange={handleOnPosDayNameChange}
                                      value={onPosDayName}
                                      sx={{ width: '100%' }}
                                    >
                                      {Object.entries(
                                       Localization.translate['days_short']
                                      ).map((day, key) => {
                                        return (
                                          <MenuItem key={key} value={key + 1}>
                                            {day[1]}
                                          </MenuItem>
                                        );
                                      })}
                                    </StyledTextField>
                                  </Grid>
                                </Grid>
                                {currentView == 'yearly' && (
                                  <Grid item xs={12} md={4} alignItems="center">
                                    <Grid item xs={12} md={6}>
                                      <p> {Localization.translate['repeat_from_month']}</p>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                      <StyledTextField
                                        select
                                        disabled={repeatOnRadio == 1}
                                        onChange={handleOnPosMonthChange}
                                        value={onPosMonth}
                                        sx={{ width: '100%' }}
                                      >
                                        {Object.entries(Localization.translate['months']).map(
                                          (month, key) => {
                                            return (
                                              <MenuItem
                                                key={key}
                                                value={key + 1}
                                              >
                                                {month[1]}
                                              </MenuItem>
                                            );
                                          }
                                        )}
                                      </StyledTextField>
                                    </Grid>
                                  </Grid>
                                )}
                              </Grid>
                            }
                          />
                        
                        </div>
                      </Stack>
                    </RadioGroup>
                  </Grid>
                )}
              </>
            )}
          </Grid>
            <Grid item xs={12}  >
              {repeatCheckbox &&
                `يحدث في ${rruleText}` 
              }
            </Grid>
         
        </Box>
      </DialogContent>
    </LocalizationProvider>
  );
});
ExceptionsDialog.displayName = 'ExceptionsDialog';
export default React.memo(ExceptionsDialog);
